import { useEffect, useState, useCallback } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Archive,
  Pin,
  ChevronLeft,
  ChevronRight,
  Music,
  Grid3X3,
  Webhook,
  Layers,
  Filter,
  X,
} from "lucide-react";
import type { AuditLogEntry } from "@wizard-sounds/shared";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const ACTION_CONFIG: Record<string, { icon: typeof Plus; color: string; label: string }> = {
  create: { icon: Plus, color: "text-moss", label: "Created" },
  update: { icon: Pencil, color: "text-spark", label: "Updated" },
  delete: { icon: Trash2, color: "text-ember", label: "Deleted" },
  archive: { icon: Archive, color: "text-fog", label: "Archived" },
  bulk_delete: { icon: Trash2, color: "text-ember", label: "Bulk deleted" },
  bulk_archive: { icon: Archive, color: "text-fog", label: "Bulk archived" },
  bulk_unarchive: { icon: Archive, color: "text-moss", label: "Bulk unarchived" },
  pin: { icon: Pin, color: "text-spark", label: "Pinned" },
};

const ENTITY_ICON: Record<string, typeof Music> = {
  sound: Music,
  combo: Layers,
  board: Grid3X3,
  webhook: Webhook,
};

const DATE_RANGES = [
  { label: "Today", days: 1 },
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "All", days: 0 },
] as const;

const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
  { value: "archive", label: "Archive" },
  { value: "pin", label: "Pin" },
  { value: "bulk_delete", label: "Bulk delete" },
  { value: "bulk_archive", label: "Bulk archive" },
  { value: "bulk_unarchive", label: "Bulk unarchive" },
];

const ENTITY_OPTIONS = [
  { value: "", label: "All types" },
  { value: "sound", label: "Sound" },
  { value: "combo", label: "Combo" },
  { value: "board", label: "Board" },
  { value: "webhook", label: "Webhook" },
];

function formatTime(ts: number) {
  const now = Date.now();
  const diff = now - ts;

  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;

  const d = new Date(ts);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: d.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
}

export function AdminAuditLog() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rangeDays, setRangeDays] = useState(7);
  const [actionFilter, setActionFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const pageSize = 30;

  const activeFilterCount =
    (actionFilter ? 1 : 0) + (entityFilter ? 1 : 0);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params: Parameters<typeof api.admin.audit>[0] = {
        limit: pageSize,
        offset: page * pageSize,
      };
      if (actionFilter) params.action = actionFilter;
      if (entityFilter) params.entity_type = entityFilter;
      if (rangeDays > 0) {
        const now = Date.now();
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        start.setDate(start.getDate() - (rangeDays - 1));
        params.from = start.getTime();
        params.to = now;
      }
      const data = await api.admin.audit(params);
      setEntries(data.entries);
      setTotal(data.total);
    } catch {}
    setLoading(false);
  }, [page, rangeDays, actionFilter, entityFilter]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [rangeDays, actionFilter, entityFilter]);

  const totalPages = Math.ceil(total / pageSize);

  const clearFilters = () => {
    setActionFilter("");
    setEntityFilter("");
  };

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold tracking-display">Audit Log</h1>
        <span className="font-mono text-[11px] text-fog">{total} events</span>
      </div>
      <p className="mt-1 text-sm text-fog">
        All actions are logged here with who did them and when.
      </p>

      {/* Date range + filter controls */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {/* Date range toggle */}
        <div className="flex rounded-xl bg-slate-raised p-0.5">
          {DATE_RANGES.map((r) => (
            <button
              key={r.label}
              onClick={() => setRangeDays(r.days)}
              className={cn(
                "rounded-lg px-3 py-1 text-xs font-medium transition-colors",
                rangeDays === r.days
                  ? "bg-spark text-ink"
                  : "text-fog hover:text-parchment",
              )}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Filter toggle button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={cn(
            "flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition-colors",
            showFilters || activeFilterCount > 0
              ? "bg-spark/20 text-spark"
              : "bg-slate-raised text-fog hover:text-parchment",
          )}
        >
          <Filter className="h-3.5 w-3.5" />
          Filters
          {activeFilterCount > 0 && (
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-spark text-[10px] font-bold text-ink">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Active filter pills */}
        {activeFilterCount > 0 && (
          <>
            {actionFilter && (
              <span className="flex items-center gap-1 rounded-full bg-slate-raised px-2.5 py-1 text-[11px] text-parchment">
                {ACTION_OPTIONS.find((a) => a.value === actionFilter)?.label}
                <button onClick={() => setActionFilter("")} className="rounded-full p-0.5 hover:bg-mist">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {entityFilter && (
              <span className="flex items-center gap-1 rounded-full bg-slate-raised px-2.5 py-1 text-[11px] text-parchment">
                {ENTITY_OPTIONS.find((e) => e.value === entityFilter)?.label}
                <button onClick={() => setEntityFilter("")} className="rounded-full p-0.5 hover:bg-mist">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            <button onClick={clearFilters} className="text-[11px] text-fog hover:text-parchment">
              Clear all
            </button>
          </>
        )}
      </div>

      {/* Expanded filter row */}
      {showFilters && (
        <div className="mt-2 flex flex-wrap items-center gap-3 rounded-xl border border-mist bg-slate p-3">
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-medium uppercase tracking-wider text-fog">Action</label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="rounded-lg border border-mist bg-slate-raised px-2 py-1 text-xs text-parchment"
            >
              {ACTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-medium uppercase tracking-wider text-fog">Type</label>
            <select
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              className="rounded-lg border border-mist bg-slate-raised px-2 py-1 text-xs text-parchment"
            >
              {ENTITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Entry list */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-spark border-t-transparent" />
        </div>
      ) : entries.length === 0 ? (
        <div className="mt-8 rounded-[18px] border border-mist bg-slate p-12 text-center">
          <p className="text-fog">
            {activeFilterCount > 0 || rangeDays > 0
              ? "No events match your filters."
              : "No audit events yet. Actions will appear here as you manage sounds, combos, boards, and webhooks."}
          </p>
        </div>
      ) : (
        <>
          <div className="mt-4 space-y-1">
            {entries.map((entry, i) => {
              const config = ACTION_CONFIG[entry.action] ?? {
                icon: Pencil,
                color: "text-fog",
                label: entry.action,
              };
              const ActionIcon = config.icon;
              const EntityIcon = ENTITY_ICON[entry.entity_type] ?? Music;
              const prevEntry = i > 0 ? entries[i - 1] : null;
              const showDateBreak =
                !prevEntry ||
                new Date(entry.created_at).toDateString() !==
                  new Date(prevEntry.created_at).toDateString();

              return (
                <div key={entry.id}>
                  {showDateBreak && (
                    <div className="flex items-center gap-3 pb-1 pt-4 first:pt-0">
                      <span className="font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
                        {new Date(entry.created_at).toLocaleDateString("en-US", {
                          weekday: "long",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      <div className="h-px flex-1 bg-mist" />
                    </div>
                  )}
                  <div className="flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-slate">
                    <div
                      className={cn(
                        "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-raised",
                        config.color,
                      )}
                    >
                      <ActionIcon className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-parchment">
                          {config.label}
                        </span>
                        <span
                          className={cn(
                            "flex items-center gap-1 rounded-full bg-slate-raised px-2 py-0.5 font-mono text-[10px] tracking-wider",
                            config.color,
                          )}
                        >
                          <EntityIcon className="h-3 w-3" />
                          {entry.entity_type}
                        </span>
                        {entry.details && (
                          <span className="truncate text-fog">
                            {entry.details}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 font-mono text-[10px] text-fog">
                        <span>{entry.actor_email}</span>
                        <span>·</span>
                        <span>{formatTime(entry.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex items-center gap-1 rounded-xl px-3 py-1.5 text-sm text-fog hover:bg-slate disabled:opacity-30"
              >
                <ChevronLeft className="h-4 w-4" /> Previous
              </button>
              <span className="font-mono text-[11px] text-fog">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1 rounded-xl px-3 py-1.5 text-sm text-fog hover:bg-slate disabled:opacity-30"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
